import React, { useState } from 'react';
import { Check, File, Files, Moon, Sun } from 'lucide-react';
import { usePricing } from '../contexts/PricingContext';
import { getBuildPaymentPlan } from '../utils/buildPaymentPlan';
import { GuaranteeCard } from '../components/GuaranteeCard';
import { FullProposal } from '../components/Proposal/FullProposal';
import { ProposalControls } from '../components/Proposal/ProposalControls';
import { BriefPrintButton } from '../components/PartnershipBrief/BriefPrintButton';
import { buildInclusions, leaderFunnelInclusion, setupIncludedNote, monthlyInclusions, salesSchoolInclusion, commitments } from '../data/advantagePricing';
const usd = (value: number) => '$' + value.toLocaleString('en-US');
type SectionProps = {
  index: number;
  label: string;
  caption: string;
  children: React.ReactNode;
};
function Section({
  index,
  label,
  caption,
  children
}: SectionProps) {
  return <section className="grid grid-cols-1 md:grid-cols-[7rem_1fr] gap-3 md:gap-6 py-7 border-t border-slate-800" data-id="element-1794" data-mp="1zc930nbo0s3f">
      <div className="md:pt-1" data-id="element-1795" data-mp="3oi57fa1hvub">
        <div className="flex items-baseline gap-1.5" data-id="element-1796" data-mp="3ldvk2dt27w9">
          <span className="text-[9px] font-bold tabular-nums text-[#7FE0C3]/60" data-id="element-1797" data-mp="3uti8ptc76l2z">
            {String(index).padStart(2, '0')}
          </span>
          <h2 className="text-[11px] font-bold uppercase tracking-widest text-[#7FE0C3]" data-id="element-1798" data-mp="3pwrjogif0f5f">
            {label}
          </h2>
        </div>
        <p className="mt-1 text-[11px] text-slate-500 leading-snug" data-id="element-1799" data-mp="mdorfca1l5kv">
          {caption}
        </p>
      </div>
      <div data-id="element-1800" data-mp="helmrsxz51df">{children}</div>
    </section>;
}
export function AdvantageSummary() {
  const {
    adSpend,
    cpaLow,
    cpaHigh,
    hasRecruiter,
    hasLeader,
    splitBuildFee,
    setHasRecruiter,
    setHasLeader,
    isGoToMarket,
    hasImplementationFee,
    hasRetentionBonuses,
    leaderBuildFee,
    leaderMonthlyFee,
    leaderActiveFee,
    leaderAdSpendLow,
    buildFee,
    scopeMultiplier,
    platformFee,
    campaignFee,
    recruiterFee,
    monthlyTotal,
    hiresLow,
    hiresHigh
  } = usePricing();

  // Light mode makes the sheet readable on screen and clean in a printed PDF.
  const [light, setLight] = useState(false);
  // The four-page proposal is the default view; the glance sheet is one click
  // away.
  const [twoPager, setTwoPager] = useState(true);
  // The side panel starts collapsed to a small tab.
  const [controlsOpen, setControlsOpen] = useState(false);
  const plan = getBuildPaymentPlan(buildFee);
  const buildAtSigning = splitBuildFee ? plan.down : buildFee;
  const buildInstallment = splitBuildFee ? plan.monthly : 0;
  const dailyAdSpend = Math.round(adSpend / 30);
  // The leader retention bonus is performance based, so it rides in the
  // "+ bonuses" language rather than the monthly fee total.
  const monthlyWithLeader = monthlyTotal;
  // With a leadership campaign, only the implementation and the first month of
  // the leadership campaign are collected up front.
  const dueAtSigning = hasLeader ? buildAtSigning + leaderBuildFee + leaderMonthlyFee : buildAtSigning + monthlyTotal;
  const phases = [{
    window: 'First 9–30 days',
    title: 'Implementation and Alignment',
    fees: `${usd(dueAtSigning)} at signing`,
    ad: 'Not running yet',
    expect: ['System, funnel, and creative built', 'Full alignment with your team', '3–4 hours of training and alignment calls']
  }, ...(hasLeader ? [{
    window: '2–8 weeks',
    title: 'Leadership Campaign',
    fees: `${usd(leaderMonthlyFee)} / mo`,
    ad: `${usd(leaderAdSpendLow)} / mo`,
    expect: ['Targeted outreach to experienced operators', 'Interviews run and vetted by D2D HIRE', 'Standard campaign launches once the leader signs']
  }] : []), {
    window: 'Month 1',
    title: 'Launch and Refinement',
    fees: hasLeader ? `${usd(monthlyTotal)} / mo` : 'Covered at signing',
    ad: `${usd(adSpend)} / mo`,
    expect: [`Your first ${hiresLow}–${hiresHigh} hires`, 'Weekly support and consulting', 'Baseline KPIs established', '30-day guarantee starts at launch']
  }, {
    window: 'Months 2–3',
    title: 'Optimization and Scale',
    fees: `${usd(monthlyWithLeader + buildInstallment)} / mo + bonuses`,
    ad: `${usd(adSpend)} / mo`,
    expect: ['Conversion rates climb', 'Retention improves and ROI shows up', 'Most partners increase investment here']
  }, {
    window: 'Month 4 onward',
    title: 'Predictable Engine',
    fees: `${usd(monthlyWithLeader)} / mo + bonuses`,
    ad: `${usd(adSpend)} / mo`,
    expect: ['Forecastable hiring output', 'Scope moves with your ad spend', 'Continuous optimization']
  }];
  return <div className={`min-h-screen w-full bg-[#0A0F1C] text-slate-200 font-sans px-5 md:px-10 py-14 md:py-16 ${twoPager ? `brief-root transition-[padding] duration-200 ease-out ${controlsOpen ? 'lg:pl-[17rem]' : 'lg:pl-20'}` : 'glance-root'} ${light && !twoPager ? 'glance-light' : ''}`} data-id="element-1801" data-mp="3k1c1o7l2z6uv">
      {twoPager && <ProposalControls open={controlsOpen} onOpenChange={setControlsOpen} data-id="element-1802" data-mp="1s8x74nxutbvy" />}
      {/* Outside the cropped frame, so they never land in an exported PDF */}
      <div className="no-print fixed top-4 right-4 z-[120] flex items-center gap-2" data-id="element-1803" data-mp="1i9pts0r4dh8u">
        {twoPager && <BriefPrintButton fileTitle={isGoToMarket ? 'D2D HIRE Launch Proposal' : 'D2D HIRE Proposal'} renderDocument={() => <FullProposal data-id="element-1805" data-mp="14hhxea3qzsao" />} className="flex items-center gap-2 rounded-lg border border-[#14684d] bg-[#14684d] px-3 py-2 text-[11px] font-bold uppercase tracking-widest whitespace-nowrap text-white transition-colors duration-150 ease-out hover:bg-[#0f5640] disabled:opacity-70 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1804" data-mp="1dgmv5e7hjhls" />}
        <button type="button" onClick={() => setTwoPager(!twoPager)} aria-pressed={twoPager} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[11px] font-bold uppercase tracking-widest whitespace-nowrap transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7a62] ${light ? 'border-slate-300 bg-white text-slate-600 hover:text-slate-900' : 'border-slate-800 bg-[#0A0F1C] text-slate-500 hover:text-slate-200'}`} data-id="element-1806" data-mp="kuevp6uksze6">
          {twoPager ? <File className="w-3.5 h-3.5" data-id="element-1807" data-mp="uuffetwcxhiw" /> : <Files className="w-3.5 h-3.5" data-id="element-1808" data-mp="ut4d6yik8htw" />}
          {twoPager ? 'Glance sheet' : 'Proposal'}
        </button>
        {!twoPager && <button type="button" onClick={() => setLight(!light)} aria-pressed={light} className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-[11px] font-bold uppercase tracking-widest transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7a62] ${light ? 'border-slate-300 bg-white text-slate-600 hover:text-slate-900' : 'border-slate-800 bg-[#0A0F1C] text-slate-500 hover:text-slate-200'}`} data-id="element-1809" data-mp="yt4podm3miyb">
          {light ? <Moon className="w-3.5 h-3.5" data-id="element-1810" data-mp="3gsxfgvbxzqow" /> : <Sun className="w-3.5 h-3.5" data-id="element-1811" data-mp="3gtyu19s62vbd" />}
          {light ? 'Dark' : 'Light'}
        </button>}
      </div>

      {twoPager ? <FullProposal data-id="element-1812" data-mp="2t5fm4vd14kjt" /> : <div className="max-w-5xl mx-auto" data-id="element-1813" data-mp="2o4i54m3r9wjw">
        {/* Header */}
        <header className="flex flex-wrap items-end justify-between gap-4 pb-6" data-id="element-1814" data-mp="2o6css86ew2ro">
          <div data-id="element-1815" data-mp="3333s6hg35eha">
            <div className="text-[11px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-1" data-id="element-1816" data-mp="32zfzfkigsw3m">
              D2D HIRE
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white" data-id="element-1817" data-mp="ias6y8489j7y">
              Your Numbers at a Glance
            </h1>
          </div>
          <div className="no-print flex items-center gap-2" data-id="element-1818" data-mp="dbp5exnghv4q">
            <button type="button" onClick={() => setHasLeader(!hasLeader)} aria-pressed={hasLeader} className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest whitespace-nowrap transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${hasLeader ? 'bg-[#7FE0C3]/10 text-[#7FE0C3] border border-[#7FE0C3]/40' : 'text-slate-500 border border-slate-800 hover:text-slate-200'}`} data-id="element-1819" data-mp="26vpaxhnz7txc">
              {hasLeader ? 'Leader search added' : 'Find a leader'}
            </button>
            <button type="button" onClick={() => setHasRecruiter(!hasRecruiter)} aria-pressed={hasRecruiter} className={`px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-widest whitespace-nowrap transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${hasRecruiter ? 'bg-amber-400/10 text-amber-400 border border-amber-400/40' : 'text-slate-500 border border-slate-800 hover:text-slate-200'}`} data-id="element-1820" data-mp="2zkmstjo9zopk">
              {hasRecruiter ? 'Recruiter added' : 'Add a recruiter'}
            </button>
          </div>
        </header>

        {/* 01 — Projection */}
        <Section index={1} label="Projection" caption="What your ad spend is expected to produce." data-id="element-1821" data-mp="nx15qnyzz9er">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 rounded-xl overflow-hidden" data-id="element-1822" data-mp="1lj8mew8kss7h">
            <div className="bg-[#0A0F1C] px-6 py-6" data-id="element-1823" data-mp="1blu40r33dtcj">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-1824" data-mp="1bkj5p6sf1dst">
                Recommended Monthly Ad Spend
              </div>
              <div className="text-4xl font-bold text-white tracking-tight" data-id="element-1825" data-mp="76v8d25hwxo">
                {usd(adSpend)}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-1826" data-mp="3rc6z4s880o6v">
                ({usd(dailyAdSpend)} / day)
              </div>
              <p className="mt-3 text-xs font-bold text-[#7FE0C3] leading-snug" data-id="element-1827" data-mp="34q3cprw927av">
                Paid directly to the job platforms. Separate from D2D HIRE's fees.
              </p>
            </div>
            <div className="bg-[#0A0F1C] px-6 py-6" data-id="element-1828" data-mp="3egxzqyhblaxd">
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2" data-id="element-1829" data-mp="39omv5ibmx0yh">
                Expected Monthly Output
              </div>
              <div className="text-4xl font-bold text-[#7FE0C3] tracking-tight" data-id="element-1830" data-mp="3ni5w3b0a01ot">
                {hiresLow}–{hiresHigh}
              </div>
              <div className="text-xs text-slate-500 mt-1" data-id="element-1831" data-mp="19czot292tzo">
                hires / month at {usd(cpaLow)}–{usd(cpaHigh)} per hire
              </div>
            </div>
          </div>
        </Section>

        {/* 02 — Investment */}
        <Section index={2} label="Investment" caption="The three components of what D2D HIRE charges." data-id="element-1832" data-mp="230q29bgqwpsk">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1833" data-mp="djm5zrjvlar8">
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col" data-id="element-1834" data-mp="dlfd17yehwzr">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-1835" data-mp="iki5zu13wcrd">
                Implementation
              </div>
              {hasImplementationFee ? <div className="text-2xl font-bold text-white" data-id="element-1836" data-mp="8qswyrhdjg9c">
                  {usd(buildFee + (hasLeader ? leaderBuildFee : 0))}
                  <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-1837" data-mp="3vx24j1qsytwh">
                    one time
                  </span>
                </div> : <>
                  <div className="text-2xl font-bold text-white" data-id="element-1838" data-mp="3lz5w4hp92d22">Included</div>
                  <p className="mt-1 text-xs text-slate-500" data-id="element-1839" data-mp="3m18jsv2xju3m">
                    {setupIncludedNote}
                  </p>
                </>}
              {hasImplementationFee && hasLeader && <p className="mt-1 text-xs text-slate-500" data-id="element-1840" data-mp="1iqlz2hylv6su">
                  Includes {usd(leaderBuildFee)} for the leader search.
                </p>}
              {splitBuildFee && <p className="mt-1 text-xs text-slate-500" data-id="element-1841" data-mp="18sfzw846oero">
                  Split: {usd(plan.down)} at signing, then {usd(plan.monthly)}{' '}
                  in months 2 and 3.
                </p>}
              <ul className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-1842" data-mp="21hmwv2nzowat">
                {buildInclusions.map(point => <li key={point} className="flex items-start" data-id="element-1843" data-mp="1wjbmmqts8js2">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-1844" data-mp="1rr1xve4elprd" />
                    <span className="text-slate-400 text-xs leading-snug" data-id="element-1845" data-mp="1npox3ig64crb">
                      {point}
                    </span>
                  </li>)}
                {hasLeader && <li className="flex items-start" data-id="element-1846" data-mp="2fh4p5s0hurox">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-1847" data-mp="2ffl8w1eu8gn5" />
                    <span className="text-white font-semibold text-xs leading-snug" data-id="element-1848" data-mp="1zt8s8za170m">
                      {leaderFunnelInclusion}
                    </span>
                  </li>}
                {hasRecruiter && <li className="flex items-start" data-id="element-1849" data-mp="b19ieei9p53s">
                    <Check className="w-3.5 h-3.5 text-amber-400 mr-2 mt-0.5 shrink-0" data-id="element-1850" data-mp="fz2rpgevaf8a" />
                    <span className="text-amber-400 text-xs leading-snug" data-id="element-1851" data-mp="kxw0ivj7li24">
                      Corporate recruiter aligned to your ICP and hiring
                      standards
                    </span>
                  </li>}
              </ul>
            </div>

            <div className="rounded-xl border border-[#7FE0C3]/40 bg-slate-900/40 p-5 flex flex-col relative" data-id="element-1852" data-mp="yp0wnnndbs7p">
              <div className="absolute top-0 left-0 w-full h-1 bg-[#7FE0C3] rounded-t-xl" data-id="element-1853" data-mp="13odiq66f0v98" />
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-1854" data-mp="18m5hnk5h9hmx">
                Monthly Service
              </div>
              <div className="text-2xl font-bold text-white" data-id="element-1855" data-mp="uxvfeenqy31o">
                {usd(monthlyTotal)}
                <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-1856" data-mp="pysm1rh4pl9j">
                  / month
                </span>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-1857" data-mp="zq53dp5awt1b">
                <div className="flex justify-between text-xs" data-id="element-1858" data-mp="c141kkqz4g50">
                  <span className="text-slate-400" data-id="element-1859" data-mp="c1ce2bj6pjfo">Partnership</span>
                  <span className="text-white" data-id="element-1860" data-mp="732i3ypmyet0">{usd(platformFee)}</span>
                </div>
                <div className="flex justify-between text-xs" data-id="element-1861" data-mp="lzjr4jacrhce">
                  <span className="text-slate-400" data-id="element-1862" data-mp="lwxcy0syi3l7">
                    Pipeline management{' '}
                    <span className="text-slate-500" data-id="element-1863" data-mp="h2jj42ty04in">({scopeMultiplier}x)</span>
                  </span>
                  <span className="text-white" data-id="element-1864" data-mp="1sfccfi2dt10t">{usd(campaignFee)}</span>
                </div>
                {hasRecruiter && <div className="flex justify-between text-xs" data-id="element-1865" data-mp="1zyljh4nf0nmp">
                    <span className="text-amber-400" data-id="element-1866" data-mp="24xy6bpeadupa">
                      Corporate recruiter{' '}
                      <span className="opacity-70" data-id="element-1867" data-mp="1qy6mv9503m3w">({scopeMultiplier}x)</span>
                    </span>
                    <span className="text-amber-400" data-id="element-1868" data-mp="1uzs2h28x6ehs">{usd(recruiterFee)}</span>
                  </div>}
              </div>
              <ul className="mt-4 pt-4 border-t border-slate-800 space-y-2" data-id="element-1869" data-mp="2iqe52e05n6ke">
                {monthlyInclusions.map(point => <li key={point} className="flex items-start" data-id="element-1870" data-mp="2nqjxvxi5uz42">
                    <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-1871" data-mp="29vzrqgz6q63x" />
                    <span className="text-slate-400 text-xs leading-snug" data-id="element-1872" data-mp="2dxuod8667vbd">
                      {point}
                    </span>
                  </li>)}
                <li className="flex items-start" data-id="element-1873" data-mp="36eowkqjyntw0">
                  <Check className="w-3.5 h-3.5 text-[#7FE0C3] mr-2 mt-0.5 shrink-0" data-id="element-1874" data-mp="36gi5ydlyks7p" />
                  <span className="text-white text-xs font-bold leading-snug" data-id="element-1875" data-mp="3g9mdp557n7nc">
                    {salesSchoolInclusion}
                  </span>
                </li>
              </ul>
            </div>

            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5 flex flex-col" data-id="element-1876" data-mp="378xxc3scgv7a">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-2" data-id="element-1877" data-mp="32acyo8sqqjky">
                Performance-Based Pricing
              </div>
              <div className="space-y-3" data-id="element-1878" data-mp="2sll7i2vi83b3">
                <div data-id="element-1879" data-mp="2sjs2st4gbt5q">
                  <div className="text-xs text-white font-medium" data-id="element-1880" data-mp="2sj8jp932eyq4">
                    Placement bonus
                  </div>
                  <div className="text-2xl font-bold text-white" data-id="element-1881" data-mp="2jkwxdj98en3h">
                    {usd(500)}
                    <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-1882" data-mp="2elajr8phr4u5">
                      per hire
                    </span>
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-1883" data-mp="29n8yqi8i8vg1">
                    {hasRetentionBonuses ? 'Day 15 active in the field' : 'One time, at day 15 active in the field'}
                  </div>
                </div>
                {hasRetentionBonuses && <div className="pt-3 border-t border-slate-800" data-id="element-1884" data-mp="29en7r1pue5ng">
                  <div className="text-xs text-white font-medium" data-id="element-1885" data-mp="2eerngpixewcm">
                    Retention bonus
                  </div>
                  <div className="text-2xl font-bold text-white" data-id="element-1886" data-mp="1gtvpdn7gcpv2">
                    {usd(1000)}
                    <span className="ml-2 text-xs font-medium text-slate-500" data-id="element-1887" data-mp="1lrf38m08n0mh">
                      per hire
                    </span>
                  </div>
                  <div className="text-xs text-slate-500" data-id="element-1888" data-mp="1vh741xyxkpze">
                    Day 45 active in the field
                  </div>
                </div>}
              </div>
              <p className="mt-4 pt-4 border-t border-slate-800 text-xs text-slate-400 leading-snug" data-id="element-1889" data-mp="1304ikml3vk31">
                Billed only when a hire is still active in the field. You pay
                for the reps you retain, instead of resumés or hires.
              </p>
            </div>
          </div>
        </Section>

        {/* 03 — Timeline */}
        <Section index={3} label="Timeline" caption="What happens when, and what is owed in each window." data-id="element-1890" data-mp="2r6liizz5fwef">
          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-800 border border-slate-800 rounded-xl overflow-hidden ${hasLeader ? 'lg:grid-cols-5' : 'lg:grid-cols-4'}`} data-id="element-1891" data-mp="305glm36wcqdi">
            {phases.map(phase => <div key={phase.window} className="bg-[#0A0F1C] px-5 py-5 flex flex-col" data-id="element-1892" data-mp="39v06tfi30wbj">
                <div className="text-sm font-bold text-[#7FE0C3]" data-id="element-1893" data-mp="3jtpr1exgasuy">
                  {phase.window}
                </div>
                <div className="text-[11px] text-slate-500" data-id="element-1894" data-mp="3evnwnxukr746">{phase.title}</div>

                <ul className="mt-3.5 pb-5 space-y-2" data-id="element-1895" data-mp="2bjvqy1uxp73">
                  {phase.expect.map(item => <li key={item} className="flex items-start" data-id="element-1896" data-mp="6dds4u50s71g">
                      <span className="w-1 h-1 rounded-full bg-[#7FE0C3]/60 mr-2.5 mt-1.5 shrink-0" data-id="element-1897" data-mp="6arimoykjhgl" />
                      <span className="text-slate-300 text-[13px] leading-snug" data-id="element-1898" data-mp="3okuu8t9885c2">
                        {item}
                      </span>
                    </li>)}
                </ul>

                <dl className="mt-auto pt-5 border-t border-slate-800 space-y-1.5 text-[11px]" data-id="element-1899" data-mp="3equkuf6wwdxj">
                  <div className="flex justify-between gap-3" data-id="element-1900" data-mp="3jl8jqrks936y">
                    <dt className="text-slate-500 shrink-0 whitespace-nowrap" data-id="element-1901" data-mp="35tk6um2pmaox">D2D HIRE</dt>
                    <dd className="text-slate-300 text-right" data-id="element-1902" data-mp="3arvfwul6kh63">{phase.fees}</dd>
                  </div>
                  <div className="flex justify-between gap-3" data-id="element-1903" data-mp="2vv4decg7mpc9">
                    <dt className="text-slate-500" data-id="element-1904" data-mp="30tfnmoaf31v0">Ads</dt>
                    <dd className="text-slate-300 text-right" data-id="element-1905" data-mp="30v0ikf0n8mte">{phase.ad}</dd>
                  </div>
                </dl>
              </div>)}
          </div>
        </Section>

        {/* 04 — Terms */}
        <Section index={4} label="Terms" caption="What we guarantee, and what we need from you." data-id="element-1906" data-mp="34u908cybh7of">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1907" data-mp="2uxmg5qdtekng">
            <GuaranteeCard size="compact" data-id="element-1908" data-mp="3io08li0balik" />
            <div className="rounded-xl border border-slate-800 bg-slate-900/40 p-5" data-id="element-1909" data-mp="3ip1mdu8ppm3z">
              <div className="text-[10px] font-bold uppercase tracking-widest text-[#7FE0C3] mb-3" data-id="element-1910" data-mp="3nncwm62x5ymq">
                What We Need From You
              </div>
              <ul className="space-y-1.5" data-id="element-1911" data-mp="38wcmzxqfryp0">
                {commitments.map((commitment, index) => <li key={commitment.title} className="flex items-start text-xs text-slate-400" data-id="element-1912" data-mp="3duxg7d79ti3y" data-mpi={index}>
                    <span className="text-[#7FE0C3] font-bold mr-2" data-id="element-1913" data-mp="a8youhbbddtd">
                      {index + 1}.
                    </span>
                    <span data-id="element-1914" data-mp="1uwj1s9b5v8s4">
                      <span className="text-white font-medium" data-id="element-1915" data-mp="1pzsn9qf08l8t">
                        {commitment.title}.
                      </span>{' '}
                      {commitment.description}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </Section>
      </div>}
    </div>;
}